import z from 'zod';
/** At most this many rows per envelope; the generator keeps the first ones by `eventId`. */
export const MODULE_EXCHANGE_EVENTS_CAP = 500;
// The graph contract declares these unions as TypeScript types only (no runtime const), so the
// literals are repeated here; `module-exchange-events-ready.webhook.test.ts` fails the compile when
// a row type and this schema drift apart.
const MODULE_EXCHANGE_BASIS = ['release-certificate', 'module-serial', 'module-list'];
/** The sub-module groups of a module exchange (DR-4652); also the `moduleGroup` of an engine configuration row (DR-5776). */
export const MODULE_EXCHANGE_GROUPS = ['fan-booster', 'hpc', 'hpt', 'lpt'];
/** The kinds of a module release (DR-5615): `OH` is `overhauled`, `RP` is `repaired`, any other status is `other`. */
export const MODULE_EXCHANGE_RELEASE_TYPES = ['overhauled', 'repaired', 'other'];
/** The kinds of record that place a donor module on a previous engine (DR-6200, `ProjectGraphDonorPlacementKind`). */
export const DONOR_PLACEMENT_KINDS = [
    'llp-sheet',
    'module-list',
    'release-certificate-ex-esn',
    'birth-record',
];
/** The kinds of document that are a record of a previous engine (DR-6200, `ProjectGraphDonorProofKind`). */
export const DONOR_PROOF_KINDS = ['nis', 'installation-removal', 'operator-statement', 'llp-sheet'];
/** The states of a donor history (DR-6200, `ProjectGraphDonorHistoryStatus`). */
export const DONOR_HISTORY_STATUSES = ['resolved', 'ambiguous', 'donor-unresolved'];
const donorBound = z.object({ after: z.iso.date().optional(), before: z.iso.date().optional() }).nullable();
const donorHistorySchema = z.looseObject({
    status: z.enum(DONOR_HISTORY_STATUSES),
    start: z.enum(['birth', 'earliest-record']),
    earliestRecord: z.object({ day: z.iso.date(), documentId: z.string(), pageIds: z.array(z.string()) }).nullable(),
    periods: z.array(z.object({
        esn: z.string().nullable(),
        arrival: donorBound,
        departure: donorBound,
        placedBy: z.array(z.object({
            kind: z.enum(DONOR_PLACEMENT_KINDS),
            day: z.iso.date().nullable(),
            documentId: z.string(),
            pageIds: z.array(z.string()),
        })),
    })),
});
const donorEngineRecordsSchema = z.array(z.object({
    esn: z.string(),
    proof: z.array(z.object({
        kind: z.enum(DONOR_PROOF_KINDS),
        statementDay: z.iso.date().nullable(),
        coverageStart: z.iso.date().optional(),
        coverageEnd: z.iso.date().optional(),
        documentId: z.string(),
        pageIds: z.array(z.string()),
    })),
}));
/**
 * A project's module exchanges (DR-5414): the `module-exchange` event nodes of the project graph
 * (DR-4652), one row per (receiving engine, day, sub-module group). Brain detects a module exchange
 * only in the graph; this family pushes the same events. Each row equals the graph event node the
 * graph route returns after the finalize that sent it: `eventId` is that node's id and the
 * consumer's upsert key.
 *
 * `confirmed`: module evidence (a release certificate, or a module row on two engines) names the
 * module and the donor. `inferred`: the LLP set turnover alone. `fromEsn` is the donor when every
 * record agrees on one; `donorCandidates` lists the donors the records disagree on, else is empty.
 * `modules` are the module parts the records print, by their printed numbers. Each module carries
 * (DR-5615) `release` from its governing release certificate: of the certificates dated on or
 * before the install day, the latest one that states a status or a counter, of any status, and is
 * a release to service (DR-5730, Rinoj 2026-10-26: a correction form, or a form whose Block 12
 * says it does not constitute or cover a release to service, never governs `release`, so an `INSP` form that refers
 * to the `RP` certificate leaves the `RP` as `release`); `null` when none qualifies or the
 * certificates of that day disagree. `counters` (DR-5671, Rinoj 2026-10-23) come from one source
 * category, never mixed: the certificates are the authority whenever one prints a counter, and the
 * latest certificate that prints a counter governs them, of any status and whether or not it is a
 * release to service (DR-5730, Rinoj 2026-10-24: a later `INSP` with other counters shows the
 * current counters), but never one dated before the latest `OH` release (an overhaul resets TSO /
 * CSO). So `counters` and `release` can come from two certificates: date the
 * counters by `countersSources[].day`, not by `release.date`. A module list row of a shop print
 * (the latest one on or before the install day that prints a counter, never one dated before the
 * latest `OH` release) supplies them only when no certificate prints a counter. A contested certificate (same-day copies that disagree) leaves
 * `counters` `null`. DR-5705: an observed exchange has no exact install day. Brain resolves each
 * possible install day: before the evidence inside the interval, each day of a certificate or a
 * list row inside the interval, and the upper bound. `counters` are set only when every possible
 * day gives the same counters. `release` is set only when those counters come from the release's
 * day, or when every possible day gives the same release. Else each is `null`. DR-5692:
 * `countersBasis` names that category (`release-certificate` or `module-list`; `null` when
 * `counters` is `null`).
 * Several readings of the governing day can each supply counters, so TSN can come from one page
 * and CSN from another: `countersSources` lists those readings, one entry per reading with the
 * counters it supplied, each counter in exactly one entry; `null` when `counters` is `null`.
 * `countersDiffer` is `true` when a module list row of the governing list day prints a different
 * cycle (`csn` or `cso`), or an hour counter (`tsn` or `tso`) more than 1 hour from a counter the
 * governing certificate prints (DR-5910; for an observed exchange, on any possible install day).
 * A smaller hour gap is the same value. `countersDifferSource`, when present, is that list row:
 * its document, page, day and four counters. Absent when there is no difference. Never null.
 * `false` means "no difference found", not
 * "verified equal": a counter only one side prints cannot differ, and a contested certificate is
 * never compared. Brain always sends `countersBasis`, `countersDiffer` and `countersSources`;
 * rows stored before DR-5692 lack them until the next finalize. `countersDifferSource` is sent
 * only with a difference. `donorEngine` is the engine type
 * the donor ESN's own sheets state: `model` is the family before the first dash, `dash` the
 * thrust rating after it with any suffix (`CFM56-7B26/3` gives `CFM56` and `7B26/3`); both
 * `null` when unknown or in conflict, `null` when the module names no donor.
 * `certificateAfterRelease` (DR-5787, Rinoj/Dzhamal 2026-10-26) is the certificate dated after
 * `release.date`, on or before the install bound `release` resolves on (the module row's own
 * `installedOn`, else the row date, else the interval's upper bound; a certificate-derived
 * `installedOn`, DR-6041, does not move it): the latest one that states a
 * status and is not a release to service, such as an `INSP` form that refers to the `RP`
 * certificate. It never holds a release to service (by DR-5730 a later one would be the release),
 * and a correction form never fills it, nor a certificate dated on a day of a correction form of
 * the module. For an observed exchange it is the latest such certificate up to the interval's upper
 * bound, not proven to be the status at install. Absent (never `null`) when none qualifies, when
 * `release` is `null`, or when that day's certificates disagree on the status. `proofDocuments` are the
 * documents that prove the event, by Brain document id, with the pages of their proving entities;
 * empty when no document proves it.
 *
 * `scope` (DR-5818, Rinoj 2026-10-28): `module` when a major-module record (a 01, 02 or 03 module
 * row, or a release certificate of the major module) names the event, or when the event has no
 * module record at all (an LLP set turnover alone); `sub-module` when it has module records and
 * none is a major module (only minor module rows or certificates changed). Brain always sends it;
 * a row without `scope` is `module` (a row sent before DR-5818). A sub-module row's `eventId` is
 * the group plus a `~sub-module` suffix (module event ids do not change). A module's optional
 * `name` is the module name a module list row prints.
 *
 * `donorHistory` (DR-6200) on a module: the engines the swapped-in module sat on before the
 * working engine, oldest first, each with the records of the working engine's own pack that place
 * it there (`placedBy`, Brain document and page ids). Brain sends evidence only and decides no
 * gap. `arrival` / `departure` bound the stay (after `after`, on or before `before`; an arrival
 * carries `before` only). `start` is `birth` when an engine's record states the module's zero life
 * and that engine is first; else the first period has `esn: null` (the time before the first
 * record). `status` `ambiguous`: the records give no one order or contradict a bound; the periods
 * still carry every record. `donor-unresolved`: the module names no donor, so no period. Each
 * engine's own records (NIS statements, installation / removal records, operator statements, LLP
 * sheets, by document category, whatever period they cover) are sent once, in the envelope's
 * `donorEngineRecords`, joined by the period's `esn` (one spelling on both sides).
 *
 * `esn` is the working engine. An empty `moduleExchanges` array retracts the project's stored rows.
 *
 * Rollout: Brain sends this family only while `MODULE_EXCHANGE_EVENTS_READY_ENABLED=true` (off by
 * default), because Oil rejects an unknown `type`.
 */
export const moduleExchangeEventsReadyWebhookSchema = z.looseObject({
    type: z.literal('module-exchange-events-ready'),
    projectId: z.string(),
    // Same rollout concession as `build-record-events-ready`: Brain always emits both stamps
    // (DR-3278, DR-3342); they are optional so a consumer that upgrades ahead of Brain still parses.
    generatedAt: z.iso.datetime().optional(),
    generation: z.number().int().positive().max(2_147_483_647).optional(),
    esn: z.string().nullable(),
    moduleExchanges: z
        .array(z.looseObject({
        eventId: z.string(),
        moduleGroup: z.enum(MODULE_EXCHANGE_GROUPS),
        // DR-5818: Brain always sends it; absent means `module` (a row sent before DR-5818).
        scope: z.enum(['module', 'sub-module']).optional(),
        moduleMajor: z.enum(['fan-booster', 'core', 'lpt']).nullable(),
        confirmation: z.enum(['inferred', 'confirmed']),
        date: z.iso.date(),
        dateBasis: z.enum(['row', 'observed']),
        // After the last sheet without the set (absent when none), on or before the first with it.
        dateInterval: z.object({ after: z.iso.date().optional(), before: z.iso.date() }).nullable(),
        fromEsn: z.string().nullable(),
        donorCandidates: z.array(z.string()),
        basis: z.array(z.enum(['llp-set', ...MODULE_EXCHANGE_BASIS])),
        modules: z.array(z.looseObject({
            serialNumber: z.string(),
            partNumber: z.string().optional(),
            // DR-5818: the module name a module list row prints; absent when no list row names it.
            name: z.string().optional(),
            fromEsn: z.string().optional(),
            basis: z.array(z.enum(MODULE_EXCHANGE_BASIS)),
            // The install date on the receiving engine: the module row's own, else (DR-6041) for an observed
            // exchange the day of the engine release certificate that closes the shop visit. Absent when neither.
            // A certificate-derived day does not move the window release / counters / certificateAfterRelease use.
            installedOn: z.iso.date().optional(),
            // DR-6064: the source of installedOn, `listed` (a module row prints it) or `engine-release-certificate`
            // (DR-6041). Absent when installedOn is absent, and on a row sent before DR-6064.
            installedOnBasis: z.enum(['listed', 'engine-release-certificate']).optional(),
            // DR-5615: Brain always sends these three, each an object or null; absent only on older rows.
            counters: z
                .object({
                tsn: z.number().nullable(),
                csn: z.number().nullable(),
                tso: z.number().nullable(),
                cso: z.number().nullable(),
            })
                .nullable()
                .optional(),
            release: z
                .object({
                type: z.enum(MODULE_EXCHANGE_RELEASE_TYPES),
                statusCode: z.string().nullable(),
                statusRaw: z.string().nullable(),
                date: z.iso.date().nullable(),
                formType: z.string().nullable(),
                source: z.object({ documentId: z.string(), pageId: z.string().nullable() }),
            })
                .nullable()
                .optional(),
            // DR-5692: Brain always sends these three; absent only on rows produced before DR-5692.
            countersBasis: z.enum(['module-list', 'release-certificate']).nullable().optional(),
            countersDiffer: z.boolean().optional(),
            // DR-5910: the list row behind countersDiffer. Absent when there is no difference. Never null.
            countersDifferSource: z
                .object({
                documentId: z.string(),
                pageId: z.string().nullable(),
                day: z.iso.date(),
                counters: z.object({
                    tsn: z.number().nullable(),
                    csn: z.number().nullable(),
                    tso: z.number().nullable(),
                    cso: z.number().nullable(),
                }),
            })
                .optional(),
            countersSources: z
                .array(z.object({
                documentId: z.string(),
                pageId: z.string().nullable(),
                day: z.iso.date(),
                counters: z.array(z.enum(['tsn', 'csn', 'tso', 'cso'])).min(1),
            }))
                .min(1)
                .nullable()
                .optional(),
            // DR-5787: the certificate dated after the release; absent, never null, when none qualifies.
            certificateAfterRelease: z
                .object({
                statusCode: z.string().nullable(),
                statusRaw: z.string().nullable(),
                date: z.iso.date(),
                source: z.object({ documentId: z.string(), pageId: z.string().nullable() }),
            })
                .optional(),
            // DR-5671: the donor ESN's stated engine type; null when the module names no donor.
            donorEngine: z.object({ model: z.string().nullable(), dash: z.string().nullable() }).nullable().optional(),
            // DR-6200: the engines the module sat on before the working engine; absent on rows sent before DR-6200.
            donorHistory: donorHistorySchema.optional(),
        })),
        title: z.string(),
        proofDocuments: z.array(z.looseObject({ documentId: z.string(), pageIds: z.array(z.string()) })),
    }))
        .max(MODULE_EXCHANGE_EVENTS_CAP),
    // DR-6200: the records of each engine a donor period names, by `esn`; absent when no module has a donor history.
    donorEngineRecords: donorEngineRecordsSchema.optional(),
});
//# sourceMappingURL=module-exchange-events-ready.webhook.js.map